import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  memo,
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import ErrorTooltip from '../../../ErrorTooltip';
import useStylesLegacy from './styles';
import {
  VGS_FIELD_ERRORS,
  VGS_FIELD_IDS,
  VGS_MOBILE_BREAKPOINT,
} from '../../../../constants/VgsFormConstants';
import { SearchStatus } from '../../../../constants';

const InputLayoutTypes = {
  COL: 0,
  ROW: 1,
};

const VGSInputForm = ({
  form,
  doShowSecurityCodeInput = true,
  doTakeCardAddressInputs = true,
  tokenizeFunc,
  inputLayout = InputLayoutTypes.COL,
  cardIndex,
  errors,
  showErrorToolTip,
}) => {
  const fieldsMountedRef = useRef(false);

  // Create unique field IDs based on cardIndex
  const fieldIdSuffix = useMemo(
    () => (cardIndex !== undefined && cardIndex !== null ? `-${cardIndex}` : ''),
    [cardIndex],
  );

  const cardNumberId = `#${VGS_FIELD_IDS.CARD_NUMBER}${fieldIdSuffix}`;
  const cardExpiryId = `#${VGS_FIELD_IDS.CARD_EXPIRY}${fieldIdSuffix}`;
  const cardCvcId = `#${VGS_FIELD_IDS.CARD_CVC}${fieldIdSuffix}`;
  const cardNameId = `#${VGS_FIELD_IDS.CARD_NAME}${fieldIdSuffix}`;
  const addressId = `#${VGS_FIELD_IDS.ADDRESS}${fieldIdSuffix}`;
  const classes = useStylesLegacy({
    isFrontFilled: false,
    isBackFilled: false,
  });

  // Initialize isMobile immediately to avoid hydration issues
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined'
      ? window.innerWidth < VGS_MOBILE_BREAKPOINT
      : false,
  );
  const {
    saveSendTripEmailStatus, saveSendTripEmailError,
  } = useSelector((store) => store.trip);
  const showErrorToolTipOnQuote = saveSendTripEmailStatus === SearchStatus.DONE
  && Boolean(saveSendTripEmailError);

  // Check if screen is mobile
  // The ROW layout type displays properly only on desktop screens wider than 1300px.
  useEffect(() => {
    const checkMobile = () => {
      const nextIsMobile = typeof window !== 'undefined'
        && window.innerWidth < VGS_MOBILE_BREAKPOINT;
      // guard to avoid unnecessary re-renders
      setIsMobile((prev) => (prev === nextIsMobile ? prev : nextIsMobile));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Use ROW layout for mobile, otherwise use the provided inputLayout
  const effectiveLayout = useMemo(
    () => (isMobile ? InputLayoutTypes.ROW : inputLayout),
    [isMobile, inputLayout],
  );
  useEffect(() => {
    if (!form || !form.field) {
      return undefined;
    }
    const containerIds = [
      `${VGS_FIELD_IDS.CARD_NUMBER}${fieldIdSuffix}`,
      `${VGS_FIELD_IDS.CARD_EXPIRY}${fieldIdSuffix}`,
      ...(doShowSecurityCodeInput
        ? [`${VGS_FIELD_IDS.CARD_CVC}${fieldIdSuffix}`]
        : []),
      `${VGS_FIELD_IDS.CARD_NAME}${fieldIdSuffix}`,
    ];

    // Check if all required fields are properly initialized
    const allFieldsMounted = containerIds.every((id) => {
      // Special handling for CVC field - it might be in different locations
      if (id === `${VGS_FIELD_IDS.CARD_CVC}${fieldIdSuffix}`) {
        const cvcElementRow = typeof document !== 'undefined' && document.querySelector(cardCvcId);
        if (!cvcElementRow) return false;
        const iframe = cvcElementRow.querySelector('iframe');
        return iframe && iframe.src && iframe.src.length > 0;
      }
      const el = typeof document !== 'undefined' && document.getElementById(id);
      if (!el) return false;
      const iframe = el.querySelector('iframe');
      return iframe && iframe.src && iframe.src.length > 0;
    });
    // If layout changed, we need to re-initialize, especially for CVC
    // Reset mounted ref when layout changes to force re-initialization
    if (allFieldsMounted && fieldsMountedRef.current) {
      // Double-check CVC field specifically when layout might have changed
      if (doShowSecurityCodeInput) {
        const cvcElement = typeof document !== 'undefined' && document.querySelector(cardCvcId);
        if (cvcElement) {
          const iframe = cvcElement.querySelector('iframe');
          // If CVC iframe doesn't exist or isn't properly initialized, force re-init
          if (!iframe || !iframe.src || iframe.src.length === 0) {
            fieldsMountedRef.current = false;
          }
        } else {
          // CVC element not found, force re-init
          fieldsMountedRef.current = false;
        }
      }
    }

    if (allFieldsMounted && fieldsMountedRef.current) {
      return undefined;
    }

    // Clear containers to ensure clean initialization
    containerIds.forEach((id) => {
      const el = typeof document !== 'undefined' && document.getElementById(id);
      if (el) el.innerHTML = '';
    });

    // Reset the mounted ref when re-initializing
    fieldsMountedRef.current = false;

    const css = {
      boxSizing: 'border-box',
      lineHeight: '1.2em',
      fontSize: '15px',
      fontWeight: '300',
      border: 'none',
      color: '#31325F',
      width: '100%',
      height: '100%',
      '&::placeholder': {
        color: '#CFD7E0',
      },
    };

    const cardNumberCss = {
      ...css,
      paddingRight: '5px',
    };

    form.field(cardNumberId, {
      type: 'card-number',
      name: 'cardNumber',
      successColor: '#4F8A10',
      errorColor: '#D8000C',
      placeholder: 'Card Number',
      showCardIcon: true,
      validations: ['required', 'validCardNumber'],
      autoComplete: 'cc-number',
      css: cardNumberCss,
    });

    form.field(cardExpiryId, {
      type: 'card-expiration-date',
      name: 'expiration',
      successColor: '#4F8A10',
      errorColor: '#D8000C',
      placeholder: 'MM / YY',
      validations: ['required', 'validCardExpirationDate'],
      autoComplete: 'cc-exp',
      css,
    });

    if (doShowSecurityCodeInput) {
      // Use querySelector to find CVC element regardless of its location in the DOM
      // This is important when layout changes (mobile to desktop or vice versa)
      const initCVCField = () => {
        const cvcElement = typeof document !== 'undefined' && document.querySelector(cardCvcId);
        if (cvcElement) {
          // Clear the element first to ensure clean initialization
          // This is especially important after layout changes or page refresh
          cvcElement.innerHTML = '';
          form.field(cardCvcId, {
            type: 'card-security-code',
            name: 'cvc',
            successColor: '#4F8A10',
            errorColor: '#D8000C',
            placeholder: 'CVC',
            validations: ['required', 'validCardSecurityCode'],
            autoComplete: 'cc-csc',
            css,
          });
        }
      };
      // Use requestAnimationFrame to ensure DOM is ready after layout change
      // This gives React time to finish rendering the new layout before we initialize
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        window.requestAnimationFrame(initCVCField);
      } else {
        initCVCField();
      }
    }

    form.field(cardNameId, {
      type: 'text',
      name: 'cardHolderName',
      placeholder: 'Full Name',
      validations: ['required'],
      autoComplete: 'cc-name',
      css: { ...css, textTransform: 'uppercase' },
    });

    if (doTakeCardAddressInputs) {
      form.field(addressId, {
        type: 'text',
        name: 'address',
        placeholder: 'Address line 1',
        autoComplete: 'address',
        css,
      });
    }

    fieldsMountedRef.current = true;
    // Cleanup function
    return () => {
      containerIds.forEach((id) => {
        const el = typeof document !== 'undefined' && document.getElementById(id);
        if (el) el.innerHTML = '';
      });
      fieldsMountedRef.current = false;
    };
  }, [
    form, doShowSecurityCodeInput, doTakeCardAddressInputs, effectiveLayout, cardIndex,
    fieldIdSuffix, cardNumberId, cardExpiryId, cardCvcId, cardNameId, addressId,
  ]);
  // Validation data used by the error tooltips
  const { state } = form || {};
  const {
    cardHolderName,
    cardNumber,
    expiration,
    cvc,
  } = errors || {};
  const {
    cardHolderName: cardHolderNameState,
    cardNumber: cardNumberState,
    expiration: expirationState,
    cvc: cvcState,
  } = state || {};

  const shouldShowErrorTooltip = (vgsState, ccValidation) => {
    if (!vgsState) return false;
    const { isFocused, isValid } = vgsState;
    return (showErrorToolTip || showErrorToolTipOnQuote)
     && !isFocused && (!isValid || Boolean(ccValidation));
  };

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (tokenizeFunc) tokenizeFunc();
      return false;
    },
    [tokenizeFunc],
  );
  const isSendQuote = !isMobile && inputLayout === InputLayoutTypes.ROW;

  return (
    <form
      id="collect-form"
      onSubmit={handleSubmit}
      action="#"
      noValidate
    >
      <div
        className={clsx(classes.frontLayout, 'w-full',
          !isMobile && 'sm:w-2/3',
          isSendQuote && 'sm:w-full border rounded-xl p-2')}
      >
        <ErrorTooltip
          title={cardHolderName || VGS_FIELD_ERRORS.cardHolderName}
          open={shouldShowErrorTooltip(
            cardHolderNameState,
            cardHolderName,
          )}
          arrow
          placement="top"
        >
          <div className="col-span-5 flex flex-col">
            <span className="text-gray-700">Cardholder name*</span>
            <div
              id={`card-name${fieldIdSuffix}`}
              className="vgs-field-container vgs-field-card-name"
            />
          </div>
        </ErrorTooltip>
        <ErrorTooltip
          title={cardNumber || VGS_FIELD_ERRORS.cardNumber}
          open={shouldShowErrorTooltip(
            cardNumberState,
            cardNumber,
          )}
          arrow
          placement={(isMobile || isSendQuote) ? 'left' : 'bottom-end'}
        >
          <div
            className={clsx(
              effectiveLayout === InputLayoutTypes.COL
                ? 'col-span-3 flex flex-col'
                : 'col-span-5 flex flex-col',
            )}
          >
            <span className="text-gray-700">
              Credit/Debit card number*
            </span>
            <div
              id={`card-number${fieldIdSuffix}`}
              className="vgs-field-container vgs-field-card-number"
            />
          </div>
        </ErrorTooltip>
        <ErrorTooltip
          title={expiration || VGS_FIELD_ERRORS.expiration}
          open={shouldShowErrorTooltip(
            expirationState,
            expiration,
          )}
          arrow
          placement={isMobile || isSendQuote ? 'bottom' : 'top'}
        >
          <div
            className={clsx(
              effectiveLayout === InputLayoutTypes.COL
                ? 'col-span-2 flex flex-col'
                : 'col-span-5 flex flex-col',
            )}
          >
            <span className="text-gray-700">Expiration date*</span>
            <div
              id={`card-expiry${fieldIdSuffix}`}
              className="vgs-field-container"
            />
          </div>
        </ErrorTooltip>
        {doShowSecurityCodeInput
          && effectiveLayout === InputLayoutTypes.ROW && (
          <ErrorTooltip
            title={cvc || VGS_FIELD_ERRORS.cvc}
            open={shouldShowErrorTooltip(cvcState, cvc)}
            arrow
            placement="right"
          >
            <div className="col-span-5 flex flex-col">
              <span className="w-full mr-3 text-gray-700">
                Security code*
              </span>
              <div className="flex items-center w-full">
                <div
                  id={`card-cvc${fieldIdSuffix}`}
                  className="vgs-field-container vgs-field-cvv"
                />
              </div>
            </div>
          </ErrorTooltip>
        )}
      </div>
      {doShowSecurityCodeInput
        && effectiveLayout === InputLayoutTypes.COL
        && !isMobile && (
        <ErrorTooltip
          title={cvc || VGS_FIELD_ERRORS.cvc}
          open={shouldShowErrorTooltip(cvcState, cvc)}
          arrow
          placement="bottom-end"
        >
          <div
            className={clsx(
              classes.backLayout,
              'vgs-back-layout-right',
            )}
          >
            <div className="h-12 bg-theme bg-opacity-10 col-span-2 justify-self-stretch self-center" />
            <div className="flex flex-col items-end col-start-2 items-baseline ml-2">
              <span className="w-full mr-3 text-gray-700">
                Security code*
              </span>
              <div
                id={`card-cvc${fieldIdSuffix}`}
                className="vgs-field-container vgs-field-cvv"
              />
            </div>
          </div>
        </ErrorTooltip>
      )}
      {doTakeCardAddressInputs && (
        <div className="hidden" id={`address${fieldIdSuffix}`} />
      )}
      <style jsx>
        {`
          .vgs-field-container {
            position: relative;
            width: 100%;
            height: 40px;
            display: flex;
            align-items: center;
            overflow: hidden;
            border-bottom: 1px solid #CFD7E0;
          }

          .vgs-field-container iframe {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border: none;
            z-index: 3;
            background: transparent;
            pointer-events: auto;
          }

          .vgs-field-cvv {
            width: auto;
            min-width: 100px;
            max-width: 150px;
            flex-shrink: 0;
          }

          @media (max-width: 1299px) {
            .vgs-field-cvv {
              min-width: 120px;
              max-width: 200px;
              width: 100%;
            }
          }

          .vgs-field-card-number {
            min-width: 350px;
            width: 100%;
            max-width: 100%;
            position: relative;
            overflow: hidden;
            border-bottom: none;
          }

          .vgs-field-card-number::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: ${inputLayout === InputLayoutTypes.ROW ? '100%' : '85%'};
            height: 1px;
            background-color: #CFD7E0;
          }

          @media (min-width: 1300px) {
            .vgs-field-card-number {
              min-width: 0;
            }
          }

          @media (max-width: 1299px) {
            .vgs-field-card-number::after {
              width: 100% !important;
            }
          }

          .vgs-field-card-number iframe {
            width: 100% !important;
            max-width: 100%;
            right: 0;
          }

          .vgs-back-layout-right {
            left: 35%;
            right: auto;
            transform: translate(0, -90%);
          }

          @media (min-width: 1300px) {
            .vgs-back-layout-right ~ * [id^="card-cvc"] {
              position: absolute;
              left: 30%;
              top: 50%;
              transform: translate(0, -50%);
            }
          }
        `}
      </style>
    </form>
  );
};

VGSInputForm.propTypes = {
  form: PropTypes.shape({
    field: PropTypes.func.isRequired,
    submit: PropTypes.func.isRequired,
    state: PropTypes.shape({
      cardHolderName: PropTypes.shape({
        isFocused: PropTypes.bool,
      }),
      cardNumber: PropTypes.shape({
        isFocused: PropTypes.bool,
      }),
      expiration: PropTypes.shape({
        isFocused: PropTypes.bool,
      }),
      cvc: PropTypes.shape({
        isFocused: PropTypes.bool,
      }),
    }),
  }),
  doShowSecurityCodeInput: PropTypes.bool,
  doTakeCardAddressInputs: PropTypes.bool,
  tokenizeFunc: PropTypes.func,
  inputLayout: PropTypes.number,
  cardIndex: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  errors: PropTypes.shape({
    cardNumber: PropTypes.string,
    expiration: PropTypes.string,
    cardHolderName: PropTypes.string,
    cvc: PropTypes.string,
  }),
  showErrorToolTip: PropTypes.bool,
};

VGSInputForm.defaultProps = {
  form: null,
  doShowSecurityCodeInput: true,
  doTakeCardAddressInputs: true,
  tokenizeFunc: null,
  inputLayout: InputLayoutTypes.COL,
  cardIndex: undefined,
  errors: {},
  showErrorToolTip: false,
};
export default memo(VGSInputForm);
